import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { vonDataService } from '@/services/vonDataService';
import { Von07Renal } from '@/types/vonData.types';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Save } from 'lucide-react';

interface Von07RenalFormProps {
  patientId: number;
  onSaved?: () => void;
  readOnly?: boolean;
}

export const Von07RenalForm: React.FC<Von07RenalFormProps> = ({
  patientId,
  onSaved,
  readOnly = false
}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasExistingData, setHasExistingData] = useState(false);

  const [renalAki, setRenalAki] = useState(false);
  const [renalDialysis, setRenalDialysis] = useState(false);
  const [renalDialysisType, setRenalDialysisType] = useState<string>('');

  useEffect(() => {
    loadData();
  }, [patientId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await vonDataService.getRenal(patientId);

      if (data) {
        setHasExistingData(true);
        setRenalAki(Boolean(data.renal_aki));
        setRenalDialysis(Boolean(data.renal_dialysis));
        setRenalDialysisType(data.renal_dialysis_type?.toString() || '');
      }
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut încărca datele',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (readOnly) return;

    try {
      setSubmitting(true);

      const formData: Partial<Von07Renal> = {
        patient_id: patientId,
        renal_aki: renalAki,
        renal_dialysis: renalDialysis,
        renal_dialysis_type: renalDialysisType ? parseInt(renalDialysisType) : undefined
      };

      if (hasExistingData) {
        await vonDataService.updateRenal(patientId, formData);
      } else {
        await vonDataService.createRenal(patientId, formData);
        setHasExistingData(true);
      }

      toast({
        title: 'Succes',
        description: 'Datele au fost salvate cu succes'
      });

      if (onSaved) {
        onSaved();
      }
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut salva datele',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Secțiunea 07: Afectare renală</CardTitle>
        <CardDescription>Date despre leziuni renale acute și dializă</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Renal conditions */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Condiții renale</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="renalAki"
                  checked={renalAki}
                  onCheckedChange={(checked) => setRenalAki(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="renalAki" className="text-sm font-normal cursor-pointer">
                  Leziune renală acută (AKI)
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="renalDialysis"
                  checked={renalDialysis}
                  onCheckedChange={(checked) => setRenalDialysis(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="renalDialysis" className="text-sm font-normal cursor-pointer">
                  Dializă efectuată
                </Label>
              </div>

              {renalDialysis && (
                <div className="space-y-2">
                  <Label htmlFor="dialysisType">Tip dializă</Label>
                  <Select value={renalDialysisType} onValueChange={setRenalDialysisType} disabled={readOnly}>
                    <SelectTrigger id="dialysisType">
                      <SelectValue placeholder="Selectează..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Peritoneală</SelectItem>
                      <SelectItem value="2">CRRT (terapie de substituție renală continuă)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>

          {!readOnly && (
            <div className="flex justify-end">
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Se salvează...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Salvează
                  </>
                )}
              </Button>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
};
